<template>
  <div id="app">
    <!-- Frontend-version update banner. Polls /version.json every 5 min
         and on tab focus; when a new build is detected, shows a non-modal
         banner the user can click to reload. JWT survives location.reload
         since it lives in localStorage. UA distribution measured
         2026-06-02: 65% 微信内置, 14% Edge, 8% Chrome, rest iOS/Android. -->
    <div v-if="show_update_banner" class="update_banner">
      <span class="update_banner_msg">
        🎉 已发布新版本，点击「刷新」加载最新功能（登录态不丢失）
      </span>
      <div class="update_banner_actions">
        <el-button size="small" type="primary" @click="reload">刷新</el-button>
        <el-button size="small" text @click="dismiss" style="color:#cbd5e1;">本次忽略</el-button>
      </div>
    </div>

    <div class="main-content" :class="{'main-content-with-banner': show_update_banner}">
      <router-view></router-view>
    </div>
    <footer class="disclaimer-footer">
      <p>免责声明: 系统中所提及的观点均基于分析师个人的独立研究，并来源于公开可获得的信息，包括但不限于公告、公开报表、行业数据、市场动态及其他公开资料。分析师已力求内容的客观、公正。</p>
      <a href="https://beian.miit.gov.cn/" target="_blank">粤ICP备2026007608号-1</a >
    </footer>
  </div>
</template>

<script setup>
import {computed} from 'vue'
import {useVersionPoll} from '@/composables/useVersionPoll'

const {update_available, banner_dismissed, reload, dismiss} = useVersionPoll()
const show_update_banner = computed(() => update_available.value && !banner_dismissed.value)
</script>

<style>
/* Inter @import removed 2026-06-02 — fonts.googleapis.com is throttled
   or blocked from mainland China (65% of normal users come in via WeChat
   on Chinese networks). The @import was a blocking stylesheet load that
   stalled the WeChat iOS progress bar at ~90% until the GFW timed it
   out. body { font-family } already lists system fallbacks (Apple SF /
   Segoe UI / Roboto) that look effectively identical to Inter for our
   UI. Self-hosting Inter is overkill for the visual delta. */

* {
  margin: 0;
  padding: 0;
}

body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  /* 微信"字体大小"设置会整体放大页面字体并破坏 fixed 布局，锁定 100% */
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
  background: var(--ac-bg);
  color: var(--ac-text);
}

.el-date-picker__time-header {
  display: none !important;
}

#app {
  height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.main-content {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 100px;
}

/* Update banner — fixed at top, sits above any sticky/fixed page chrome. */
.update_banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: linear-gradient(90deg, #1e293b 0%, #334155 100%);
  color: #f1f5f9;
  padding: 10px 16px;
  /* 刘海屏（微信全屏模式）：横幅内容避开状态栏安全区 */
  padding-top: calc(10px + env(safe-area-inset-top, 0px));
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.25);
  gap: 12px;
  flex-wrap: wrap;
}

.update_banner_msg {
  flex: 1 1 auto;
  min-width: 200px;
}

.update_banner_actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.main-content-with-banner {
  padding-top: 48px;
}

@media (max-width: 480px) {
  .update_banner {
    font-size: 12px;
    padding: 8px 12px;
  }
  .main-content-with-banner {
    padding-top: 76px;  /* allow 2-line wrap on phones */
  }
}

.disclaimer-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  background: var(--ac-footer-bg);
  backdrop-filter: blur(12px);
  text-align: center;
  padding: 10px 20px;
  /* 刘海屏底部 Home 指示条安全区（iPhone 微信全屏） */
  padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--ac-border);
  z-index: 1000;
  box-sizing: border-box;
}

.disclaimer-footer p {
  font-size: 11px;
  color: #94a3b8;
  margin: 0;
  line-height: 1.5;
}

.disclaimer-footer a {
  font-size: 11px;
  color: #94a3b8;
  text-decoration: none;
}

.disclaimer-footer a:hover {
  color: #3b7ddd;
}

/* ── Global Element-Plus overrides（颜色一律走 theme.css 变量，dark 自动生效）── */
.el-card {
  border-radius: 12px !important;
  border: 1px solid var(--ac-border) !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important;
}

.el-card__header {
  border-bottom: 1px solid var(--ac-border-soft) !important;
  padding: 14px 20px !important;
}

/* 仅覆盖实心 primary。历史版本无条件覆盖所有 .el-button--primary，
   把 plain（淡底蓝字）也刷成实心蓝底，而 plain 的文字颜色仍是蓝色 →
   蓝底蓝字不可见、hover 文字变白才显形（2026-06b 用户报告：ranking 页
   「返回首页」按钮）。排除 plain/text/link 变体让 Element Plus 自己渲染。 */
.el-button--primary:not(.is-plain):not(.is-text):not(.is-link) {
  background-color: var(--ac-primary) !important;
  border-color: var(--ac-primary) !important;
}

.el-button--primary:not(.is-plain):not(.is-text):not(.is-link):hover {
  background-color: var(--ac-primary-hover) !important;
  border-color: var(--ac-primary-hover) !important;
}

.el-table th.el-table__cell {
  background-color: var(--ac-table-header-bg) !important;
  color: var(--ac-table-header-text) !important;
  font-weight: 600 !important;
  font-size: 13px !important;
}

@media (max-width: 640px) {
  .main-content {
    padding-bottom: 120px;
  }

  .disclaimer-footer {
    padding: 8px 12px;
  }

  .disclaimer-footer p {
    font-size: 10px;
  }
}
</style>
